// Batch 28 · item 14 (M51): explicit Save / Cancel for a screen that used to save on every change. Nothing is written until
// Save; Cancel puts back the last saved values; while there are unsaved changes the bar says so, closing / reloading the tab
// asks first and an in-app link asks "Leave without saving?" (the app's own dialog).
// Batch 28 · Follow-up 1 · F2: the dirty flag also goes into the app-wide registry (lib/unsavedChanges) with this bar's Save —
// the engagement switch asks first and the session warning can "Save now".
import React, { useEffect, useId, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { askConfirm } from "@/lib/appDialog";
import { setUnsaved, UNSAVED_TEXT } from "@/lib/unsavedChanges";
import { useSaveBarPresence } from "@/lib/saveBarPresence";   // Batch 29 · M70

export const SCB_TEXT = {
  en: { save: "Save", cancel: "Cancel", unsaved: "Unsaved changes", saved: "All changes saved", saving: "Saving…", savedOk: "Saved",
        leave: UNSAVED_TEXT.en.leave },
  ar: { save: "حفظ", cancel: "إلغاء", unsaved: "تغييرات غير محفوظة", saved: "حُفظت كل التغييرات", saving: "جارٍ الحفظ…", savedOk: "تم الحفظ",
        leave: UNSAVED_TEXT.ar.leave },
};

/** While ``dirty``: the browser asks before closing / reloading, an in-app link asks first, and the registry knows (with
    ``save`` for the session warning's "Save now"). */
export function useUnsavedGuard(dirty, lang = "en", save = null) {
  const navigate = useNavigate();
  const key = useId();
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    setUnsaved(key, !!dirty, dirty && saveRef.current ? () => saveRef.current && saveRef.current() : null);
    return () => setUnsaved(key, false);
  }, [dirty, key]);
  useEffect(() => {
    if (!dirty) return undefined;
    const L = SCB_TEXT[lang === "ar" ? "ar" : "en"];
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = L.leave; return L.leave; };
    const onClick = (e) => {
      const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!a || a.target === "_blank" || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || (url.pathname === window.location.pathname && url.search === window.location.search)) return;
      e.preventDefault(); e.stopPropagation();
      askConfirm(L.leave).then((ok) => { if (ok) navigate(url.pathname + url.search + url.hash); });
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => { window.removeEventListener("beforeunload", onBeforeUnload); document.removeEventListener("click", onClick, true); };
  }, [dirty, lang, navigate]);
}

export default function SaveCancelBar({ dirty, saving = false, onSave, onCancel, lang = "en", disabled = false, testid = "save-cancel" }) {
  const L = SCB_TEXT[lang === "ar" ? "ar" : "en"];
  useUnsavedGuard(dirty, lang, onSave);
  useSaveBarPresence();
  return (
    // Step 11c-3 · find 30: with a table bar beside it, the bar that holds a change is drawn on top (never hidden by a clean bar)
    <div className={`sticky bottom-0 ${dirty ? "z-20" : "z-10"} flex flex-wrap items-center justify-end gap-2 rounded border px-3 py-2 text-xs ${dirty ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30" : "bg-white dark:bg-slate-900"}`}
         data-testid={testid} data-dirty={dirty ? "1" : "0"} role="status">
      <span className={dirty ? "font-medium text-amber-900 dark:text-amber-100" : "text-muted-foreground"} data-testid={`${testid}-state`}>
        {saving ? L.saving : dirty ? L.unsaved : L.saved}</span>
      <Button size="sm" variant="outline" disabled={!dirty || saving || disabled} onClick={onCancel} data-testid={`${testid}-cancel`}>{L.cancel}</Button>
      <Button size="sm" disabled={!dirty || saving || disabled} onClick={onSave} data-testid={`${testid}-save`}>{L.save}</Button>
    </div>
  );
}
